import { PermissionLevel } from '@/client/custom-roles';
import { SelectButton } from 'primereact/selectbutton';
import React from 'react';
import styled, { css } from 'styled-components';

export type SegmentedControlOption = {
  label: string;
  value: PermissionLevel;
};

export type FormikSegmentedControlProps = {
  field: {
    name: string;
    value: PermissionLevel;
    onChange: (event: unknown) => void;
    onBlur: () => void;
  };
  form: {
    setFieldValue: (name: string, value: unknown) => void;
  };
  options: SegmentedControlOption[];
  disabled?: boolean;
  ariaLabel?: string;
};

export const FormikSegmentedControl: React.FC<FormikSegmentedControlProps> = ({
  field,
  form,
  options,
  disabled,
  ariaLabel,
}) => (
  <StyledSelectButton
    id={field.name}
    name={field.name}
    value={field.value}
    options={options}
    optionLabel="label"
    optionValue="value"
    disabled={disabled}
    aria-label={ariaLabel}
    allowEmpty={false}
    unselectable={false}
    $level={field.value}
    onChange={(event) => {
      if (event.value === null || event.value === undefined) {
        return;
      }

      form.setFieldValue(field.name, event.value);
    }}
  />
);

const highlight = (level: PermissionLevel) => {
  if (level === PermissionLevel.VIEW) {
    return css`
      background-color: var(--blue-light);
    `;
  }

  if (level === PermissionLevel.EDIT) {
    return css`
      background-color: var(--green-edit-bg);
    `;
  }

  return css`
    background-color: var(--beige-main);
  `;
};

const StyledSelectButton = styled(SelectButton)<{ $level: PermissionLevel }>`
  &.p-selectbutton {
    display: inline-flex;
    gap: var(--xxxsmall-padding);
    padding: var(--xxsmall-padding); /* Figma 3px -> --xxsmall-padding (4px) */
    border: 1px solid var(--gray-dark);
    border-radius: var(--xsmall-border-radius);
    background-color: var(--white-main);
    width: max-content;

    .p-button {
      padding: var(--semi-xsmall-padding) var(--medium-padding);
      border: none;
      border-radius: var(--box-border-radius);
      background-color: transparent;
      color: var(--gray-medium-dark);
      font-size: var(--xsmall-font-size);
      line-height: normal;

      .p-button-label {
        font-weight: 500; /* PrimeReact defaults the label span to 700 */
      }
      transition: background-color 0.15s ease-in-out;

      &:focus {
        box-shadow: none;
      }

      &:not(.p-highlight):not(.p-disabled):hover {
        background-color: var(--beige-main);
        color: var(--gray-medium-dark);
      }

      &.p-highlight {
        color: var(--black-main);
        ${({ $level }) => highlight($level)}

        &:hover {
          color: var(--black-main);
          ${({ $level }) => highlight($level)}
        }
      }
    }

    &.p-disabled,
    .p-button.p-disabled {
      opacity: 0.6;
    }
  }
`;
